<!--

    !!
    This component is dependent on having the entire viewports width,
    as it has logic based on the window width!
    !!

    This component should be at the root of every view
    as it should handle all layout logic

    One use case example:
        <MainLayout>
            <SomeContent slot='center'>
            </SomeContent>

            <SomeOtherContent slot='left'>
            </SomeOtherContent>
        </MainLayout>

    Features:
        * MainLayout will automatically handle putting several elements in a named slot
            * MainLayout does not currently handle several elements in the 'top' slot
            f.eks:
                <MainLayout>
                    <SomeContent slot='center' />

                    <SomeOtherContent slot='center' />

                    <SidebarContent slot='left' />

                    <MoreSidebarContent slot='left' />
                </MainLayout>

        * MainLayout will automatically make center fill the entire grid width (1000px)
          when no 'right' is defined

        * MainLayout will automatically place the 'left' sidebar at the top of 'right'
          when there is no longer space at the left of the screen




    * names in outline below are named slots that should be used
    * in views to describe what elements should go where

    This is how the named slots will be placed in the grid
    |--------------------------------------------------------------------|
    |    '332px'                     ''1000px''                          |
    |  |---------|  |-----------------------------------------|          |
    |  |  'left' |  |                   'top'                 |          |
    |  |         |                                                       |
    |               |-----------------------| |---------------|          |
    |               |        'center'       | |    'right'    |          |
    |               |                       | |    'right'    |          |

    grid-spacing: 20px

    spacing and width values should be loaded from theme, the values in outline
    will only be used for prototyping
 -->

<template>
    <div class="main-grid" :class="{ marginTop: marginTop }">
        <div :class="['center', right]">
            <div v-if="top" :style="right ? 'grid-column: span 2' : ''">
                <slot v-if="!hideMap['top']" :name="slot('top')"></slot>
            </div>
            <div class="column">
                <slot v-if="!hideMap['center']" :name="slot('center')"></slot>
            </div>
            <div v-if="right || isMobile" class="column">
                <template v-if="!leftToBottomRight">
                    <slot
                        v-if="left && !spaceForLeft && !hideMap['left']"
                        :name="slot('left')"
                    ></slot>
                    <slot v-if="!hideMap['right']" :name="slot('right')"></slot>
                    <slot
                        v-if="
                            bottomLeftToBottomRight &&
                                !spaceForLeft &&
                                !hideMap['bottomLeftToBottomRight']
                        "
                        :name="slot('bottomLeftToBottomRight')"
                    ></slot>
                </template>
                <template v-else>
                    <slot :name="slot('right')" v-if="!hideMap['right']"></slot>
                    <slot
                        v-if="left && !spaceForLeft && !hideMap['left']"
                        :name="slot('left')"
                    ></slot>
                    <slot
                        v-if="
                            bottomLeftToBottomRight &&
                                !spaceForLeft &&
                                !hideMap['bottomLeftToBottomRight']
                        "
                        :name="slot('bottomLeftToBottomRight')"
                    ></slot>
                </template>
            </div>
        </div>
        <div v-if="left && spaceForLeft" class="left column">
            <slot name="left" v-if="!hideMap['left']" />
            <slot name="bottomLeftToBottomRight" v-if="!hideMap['bottomLeftToBottomRight']" />
        </div>
    </div>
</template>

<script>
export default {
    name: 'MainLayout',
    props: {
        marginTop: Boolean, // For adding top margin
        leftToBottomRight: Boolean,
        hideOnMobile: String,
    },
    data: () => ({
        windowWidth: window.innerWidth,
    }),
    computed: {
        hideMap() {
            return (
                this.isMobile &&
                (this.hideOnMobile || '')
                    .split(' ')
                    .reduce((acc, val) => ({ ...acc, [val]: true }), {})
            );
        },
        right: function() {
            return (this.$slots.right || (this.left && !this.spaceForLeft)) && !this.isMobile
                ? 'right'
                : null;
        },

        left: function() {
            return this.$slots.left ? 'left' : null;
        },

        bottomLeftToBottomRight: function() {
            return this.$slots.bottomLeftToBottomRight ? 'bottomLeftToBottomRight' : null;
        },

        top: function() {
            return this.$slots.top ? 'top' : null;
        },

        spaceForLeft: function() {
            return this.windowWidth > 1080 + 4 * 18 + 2 * (1080 / 3);
        },

        isMobile: function() {
            return this.windowWidth < 1080 + 2 * 18;
        },
    },
    mounted() {
        window.addEventListener('resize', () => {
            this.windowWidth = window.window.innerWidth;
        });
    },
    methods: {
        slot(name) {
            if (!this.isMobile) {
                return name;
            } else {
                return this.$slots['mob' + name] ? 'mob' + name : name;
            }
        },
    },
};
</script>

<style scoped>
.main-grid {
    /* Since media queries don't support css variables i have choosen to hardcode the width variable here,
       to avoid weird behaviour later on if we choose to change the grid width.
       To change the grid width, update the value in the media query to reflect the new width in pixels
          PS. Accessing css variables in js is possible but quite weird, so the width is also hardcoded in
              the js above, so remember to change that aswell   -- see the spaceForLeft computed value -- */

    display: grid;
    grid-template-columns:
        auto var(--grid-third) var(--grid-width) var(--grid-third)
        auto;
    grid-template-areas: '....       left              center            .....       ....';
    grid-gap: var(--grid-gap);
    grid-auto-rows: min-content;
    margin-bottom: 30px;
}
@media screen and (max-width: 1872px) {
    /*calc(1080px + 4*18px + 2*(1080px/3)*/
    .main-grid {
        grid-template-columns: auto var(--grid-width) auto;
        grid-template-areas: '....      center       ....';
    }
}

.center {
    display: grid;
    grid-gap: inherit;
    grid-template-columns: 1fr;
    grid-area: center;
}
.center.right {
    grid-template-columns: 2fr 1fr;
    grid-auto-rows: min-content;
}

.column {
    display: grid;
    grid-auto-flow: row;
    grid-auto-rows: min-content;
    grid-gap: inherit;
}

.left {
    grid-area: left;
}

@media screen and (max-width: 1116px) {
    /*calc(1080px + 2*18px) fuk webkit*/
    .main-grid {
        grid-template-columns: 1fr;
        grid-template-areas: 'center';
    }
    .center.right {
        grid-template-columns: 1fr;
    }
}

.marginTop {
    margin-top: var(--grid-mt);
}
</style>
